/**
 * OpenAI Pixel Block
 *
 * Injection block that loads the OpenAI ads pixel (oaiq) tracking script.
 * Site-wide: same pixel ID across all pages.
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import { useTrackingAllowed } from '../../shared/analytics';

const SCRIPT_ID = 'openai-pixel-script';

function OpenAiPixelComponent(_props: BlockProps) {
  const pixelId = useBlockToken('pixel_id', { defaultValue: '' });
  const debugToken = useBlockToken('debug', { defaultValue: 'true' });
  const debugEnabled = debugToken !== 'false';
  const trackingAllowed = useTrackingAllowed('advertising');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!trackingAllowed || !pixelId) return;

    if (document.getElementById(SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.textContent = `!function(w,d,s,u){if(w.oaiq)return;var q=function(){q.q.push(arguments)};q.q=[];w.oaiq=q;var j=d.createElement(s);j.async=1;j.src=u;var f=d.getElementsByTagName(s)[0];f.parentNode.insertBefore(j,f)}(window,document,"script","https://bzrcdn.openai.com/sdk/oaiq.min.js");oaiq("init",{pixelId:"${pixelId}",debug:${debugEnabled}});`;
    document.head.appendChild(script);

    return () => {
      mountedRef.current = false;
    };
  }, [trackingAllowed, pixelId, debugEnabled]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'OpenAiPixel',
  displayName: 'OpenAI Pixel',
  tokens: {
    pixel_id: {
      label: 'Pixel ID',
      type: 'text',
      description: 'OpenAI pixel ID (alphanumeric string).',
      required: true,
    },
    debug: {
      label: 'Debug',
      type: 'text',
      defaultValue: 'true',
      description:
        'When "true" (default), initializes the pixel with debug logging enabled. Set to "false" to disable.',
    },
  },
};

export const openAiPixelBlock: BlockDefinition = {
  id: 'openai-pixel',
  name: 'OpenAI Pixel',
  description: 'OpenAI ads conversion tracking pixel. Loads the oaiq SDK and initializes it with the configured pixel ID.',
  category: 'Analytics',
  placement: 'injection',
  defaultScope: 'global',
  component: OpenAiPixelComponent,
  contentSchema,
  tags: ['openai', 'oaiq', 'pixel', 'tracking', 'ads', 'conversion'],
};
